/**
 * The saved screens a user can put in a tab — theirs, their clubs', and the
 * product's.
 *
 * **Module scope with subscribers, not a context** — `lib/symbol-index.ts`'s
 * shape and `lib/track-lists.ts`'s reason. The tab strip, the screener panel and
 * the presets tab all need the same answers, and a copy per component is a set
 * of copies that can disagree: the visible symptom would be a tab strip offering
 * a screen the panel cannot open. A provider would also put a re-render boundary
 * around the shell to publish something the grid must not re-render on.
 *
 * ## One listing, three owners
 *
 * `/presets` answers all three kinds in one call, because they are one table
 * with an `owner_kind`. What differs is what a surface may *do* with each:
 *
 *     user     rename, delete, and edit the definition
 *     org      select it; the club's half is theirs (step 4)
 *     system   likewise, and it belongs to the product
 *
 * ## What "the first tab is Default" means structurally
 *
 * It is a **position, not a protected identity**. User screens are ordered by
 * key, and a key is a ULID, so that is creation order — the screen migrated out
 * of each panel's inline definition is the oldest and therefore first. Nothing
 * carries a `isDefault` flag, which means there is no state where the flag and
 * the ordering disagree, and no migration if somebody deletes the one holding
 * it.
 *
 * The invariant that actually matters is the one the flag was for: **you cannot
 * delete your last screen**, so there is always a tab to fall back to. That is
 * `canDeleteScreen`, and it is about the count rather than about which one.
 */

import { useEffect, useReducer } from "react";
import {
  createScreen as createOnServer,
  deleteScreen as deleteOnServer,
  fetchPresets,
  renameScreen as renameOnServer,
  type NewScreen,
  type ScreenSummary,
} from "../api/presets";
import { groupDisplayName } from "./group-layouts";

export type Screen = ScreenSummary;

/** Yours to rename, delete and edit. */
export function isOwn(screen: Screen): boolean {
  return screen.ownerKind === "user";
}

/**
 * A screen this account is **authoring** — the group's own screen, while acting
 * as that group's account.
 *
 * `isOwn` is `ownerKind === "user"`, so in Author as *Persons* (`org`,
 * `grp-jp`) read as a club's screen though the account editing it *is* the
 * club: its edits went to the author's overlay, which has no room for a
 * format, and were gone on the next reopen (owner, 2026-10-06). An authored
 * screen is edited as a **draft** instead — `lib/screen-draft.ts`.
 *
 * `publishTo` is `/auth/me`'s, the one answer to "which group am I authoring";
 * null for everybody not in Author as, so a member never matches.
 */
export function isAuthored(screen: Screen, publishTo: string | null | undefined): boolean {
  return screen.ownerKind === "org" && !!publishTo && screen.ownerKey === publishTo;
}

/**
 * Whose sealed conditions a panel shows — the screen owner's name as a member
 * reads it (`JP`), for the 🔒's "JP's rule — applied on the server". Undefined
 * for a screen with no group owner, which the picker words generically.
 */
export function sealedByName(
  screens: readonly Screen[],
  screenRef: string | undefined,
): string | undefined {
  const screen = screenRef ? screens.find((s) => s.key === screenRef) : undefined;
  if (!screen || !screen.ownerKey) return undefined;
  return groupDisplayName(screen.ownerKey, screen.ownerName);
}

/** A club's — selectable, never editable here. */
export function isClub(screen: Screen): boolean {
  return screen.ownerKind === "org";
}

/**
 * Tab order: yours oldest-first, then your clubs', then the product's.
 *
 * Yours by **key**, which is a ULID and therefore creation order — so the strip
 * does not reshuffle when you rename one, which a name sort would do while you
 * were typing.
 *
 * Club and system screens by name, because their keys are slugs somebody chose
 * and carry no order worth honouring.
 */
export function orderTabs(screens: readonly Screen[]): Screen[] {
  const byName = (a: Screen, b: Screen) => a.name.localeCompare(b.name);
  return [
    ...screens.filter(isOwn).sort((a, b) => a.key.localeCompare(b.key)),
    ...screens.filter(isClub).sort(byName),
    ...screens.filter((s) => s.ownerKind === "system").sort(byName),
  ];
}

/** The tab a panel falls back to: your oldest screen, or the first of anything. */
export function defaultScreen(screens: readonly Screen[]): Screen | undefined {
  return orderTabs(screens)[0];
}

/**
 * Whether this screen may be deleted.
 *
 * Yours, and not the last one you have. A club's is never deletable here —
 * leaving the club is what removes it — and the server answers 404 to a delete
 * aimed at one, so this is the message rather than the enforcement.
 */
export function canDeleteScreen(screens: readonly Screen[], key: string): boolean {
  const screen = screens.find((s) => s.key === key);
  if (!screen || !isOwn(screen)) return false;
  return screens.filter(isOwn).length > 1;
}

/** A name not already taken, since `uq_preset_owner_name` refuses a duplicate. */
export function nextFreeName(screens: readonly Screen[], word = "Screen"): string {
  const taken = new Set(screens.filter(isOwn).map((s) => s.name));
  if (!taken.has(word)) return word;
  let n = 2;
  while (taken.has(`${word} ${n}`)) n += 1;
  return `${word} ${n}`;
}

// ── the store ───────────────────────────────────────────────────────────────

let held: Screen[] = [];
let version = 0;
let failure: string | null = null;
const listeners = new Set<() => void>();

function publish(): void {
  version += 1;
  for (const notify of listeners) notify();
}

export function screensSnapshot(): readonly Screen[] {
  return held;
}

export function screensError(): string | null {
  return failure;
}

export function screenFor(key: string | undefined): Screen | undefined {
  return key ? held.find((s) => s.key === key) : undefined;
}

/**
 * Read the catalog.
 *
 * Failure is an **empty list and a message**, never a throw: the listing is
 * chrome, and a strip that cannot draw is better than a shell that will not
 * render. Writes are the opposite — see `api/presets.ts`.
 */
export async function loadScreens(): Promise<void> {
  try {
    held = await fetchPresets();
    failure = null;
  } catch (cause) {
    held = [];
    failure = cause instanceof Error ? cause.message : "could not read your screens";
  }
  publish();
}

/**
 * Create one, and return it so the caller can select it.
 *
 * **Not optimistic**, unlike a star in a track list: the server mints the key,
 * and a tab drawn before it has one cannot be selected, renamed or deleted —
 * so the only thing an optimistic insert would buy is a tab that rejects every
 * click for a moment.
 */
export async function addScreen(input: NewScreen): Promise<Screen> {
  const made = await createOnServer(input);
  held = [...held, made];
  publish();
  return made;
}

/**
 * Rename one, optimistically, and put the old name back if it is refused.
 *
 * Optimistic here and not above because the row already exists: the name is the
 * only thing changing, and a tab that shows the old name for a round trip while
 * the user types is the lag the track lists' stars were made optimistic to
 * avoid.
 */
export async function renameScreen(key: string, name: string): Promise<void> {
  const before = held;
  held = held.map((s) => (s.key === key ? { ...s, name } : s));
  publish();
  try {
    await renameOnServer(key, name);
  } catch (cause) {
    held = before;
    failure = cause instanceof Error ? cause.message : "that rename did not save";
    publish();
    throw cause;
  }
}

/** Delete one, optimistically, and put it back if the server refuses. */
export async function removeScreen(key: string): Promise<void> {
  const before = held;
  held = held.filter((s) => s.key !== key);
  publish();
  try {
    await deleteOnServer(key);
  } catch (cause) {
    held = before;
    failure = cause instanceof Error ? cause.message : "that screen was not deleted";
    publish();
    throw cause;
  }
}

/**
 * Whether a screen holds an unpublished draft — the tab's ✎.
 *
 * **Kept current here, not re-read.** The listing says `hasDraft` once; after
 * that the panel knows sooner than the server could be asked — a save creates
 * the draft, Discard and Publish end it — so it says so locally rather than
 * spending a `/presets` round trip on a pencil.
 */
export function markDraft(key: string, hasDraft: boolean): void {
  const screen = held.find((s) => s.key === key);
  if (!screen || Boolean(screen.hasDraft) === hasDraft) return;
  held = held.map((s) => (s.key === key ? { ...s, hasDraft } : s));
  publish();
}

/** A draft was published: the version moved and the draft is gone. */
export function markPublished(key: string, version: number | undefined): void {
  held = held.map((s) =>
    s.key === key
      ? { ...s, hasDraft: false, ...(typeof version === "number" ? { version } : {}) }
      : s,
  );
  publish();
}

/** Test seam: put the store back where a fresh page would have it. */
export function resetScreens(next: readonly Screen[] = []): void {
  held = [...next];
  failure = null;
  publish();
}

/** Subscribe to the catalog. Returns the unsubscribe. */
export function subscribeScreens(notify: () => void): () => void {
  listeners.add(notify);
  return () => listeners.delete(notify);
}

/**
 * The catalog, as a hook — and the thing that loads it.
 *
 * `useTrackLists`'s shape: the first consumer to mount asks for the catalog, so
 * no caller has to remember to, and `loadScreens` is idempotent enough that the
 * second and third cost one request each at worst rather than a bug.
 */
export function useScreens(): { screens: readonly Screen[]; error: string | null } {
  const [, bump] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    listeners.add(bump);
    void loadScreens();
    return () => {
      listeners.delete(bump);
    };
  }, []);

  return { screens: held, error: failure };
}
